// Components/Navbar/Navbar.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import Apiurl from "../../Environment/Apiurl";
import {
  Search,
  Bell,
  ChevronDown,
  Sun,
  Moon,
  SunMoon,
  LogOut,
  Palette,
  X,
  User,
  Users,
  Wallet,
  ClipboardList,
  Building,
  IdCard,
  Loader2,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import "./Navbar.css";
import { usePermissions } from "../../context/PermissionContext";
import NotificationPanel from "../NotificationPanel/NotificationPanel";
import { CiLocationOn } from "react-icons/ci";

/* ================================================================
   FOOTER TEXT — CHANGE THESE TWO LINES TO YOUR ACTUAL BRANDING
================================================================ */
const FOOTER_DEVELOPER_NAME = "Aalan TechSoft";
const FOOTER_DEVELOPER_LOCATION = "Salem"; // set to '' to omit

/* ================= ERP BACKGROUND CONFIG ================= */
const ERP_LIGHT_DEFAULT = "#f7f8fa";
const ERP_DARK_DEFAULT = "#0f172a";

const LIGHT_COLORS = [
  "#ffffff",
  "#f7f8fa",
  "#f1f5f9",
  "#eef2ff",
  "#f0fdf4",
  "#fff7ed",
  "#ecfeff",
  "#fdf2f8",
  "#faf5ff",
  "#fffbeb",
  "#f8fafc",
  "#fee2e2",
  "#fecaca",
  "#fca5a5",
  "#ffebee",
  "#fff1f2",
];

const DARK_COLORS = [
  "#020617",
  "#0f172a",
  "#111827",
  "#1e293b",
  "#0b1220",
  "#020c1b",
  "#121212",
  "#0a0f1f",
  "#1a1a1a",
  "#081a1a",
  "#2a0f14",
  "#3b0d12",
  "#450a0a",
  "#1f0a0a",
  "#2b0a0a",
];

/* ================= GLOBAL SEARCH CONFIG ================= */
// Icon + section label per result "type" the backend /search route returns.
// Add an entry here for every module you want searchable.
const SEARCH_TYPE_META = {
  employee: { label: "Employees", icon: Users, color: "#16a34a" },
  user: { label: "Users", icon: User, color: "#2563eb" },
  salary: { label: "Salaries", icon: Wallet, color: "#d97706" },
  task: { label: "Tasks", icon: ClipboardList, color: "#7c3aed" },
  branch: { label: "Branches", icon: Building, color: "#0891b2" },
  idcard: { label: "ID Cards", icon: IdCard, color: "#db2777" },
};
const SEARCH_RESULTS_PER_CATEGORY = 5;

// ═══════════════════════════════════════════════════════════════
//   BREADCRUMB CONFIG
// ═══════════════════════════════════════════════════════════════
const SEGMENT_CONFIG = {
  // ── top-level segments ──────────────────────────────────────
  "": { label: "Dashboard", navigateTo: "/" },
  master: { label: "Master", navigateTo: null },
  hr: { label: "HR", navigateTo: null },
  leads: { label: "Leads", navigateTo: null },
  inventory: { label: "Inventory", navigateTo: null },
  carpenters: { label: "Carpenters", navigateTo: null },
  sales: { label: "Sales", navigateTo: "/sales" },
  projects: { label: "Projects", navigateTo: "/projects" },
  clients: { label: "Clients", navigateTo: "/clients" },
  reports: { label: "Reports", navigateTo: "/reports" },
  tracking: { label: "Tracking", navigateTo: null },
  vehicles: { label: "Vehicles", navigateTo: "/tracking/vehicles" },

  // ── master sub-pages ────────────────────────────────────────
  users: { label: "Users", navigateTo: "/master/users" },
  branch: { label: "Branch", navigateTo: "/master/branch" },
  department: { label: "Department", navigateTo: "/master/department" },
  "employee-role": {
    label: "Employee Role",
    navigateTo: "/master/employee-role",
  },
  brand: { label: "Brand Master", navigateTo: "/master/brand" },
  roles: { label: "Roles", navigateTo: "/master/roles" },
  shift: { label: "Shift", navigateTo: "/master/shift" },
  colors: { label: "Colors", navigateTo: "/master/colors" },

  // ── HR sub-pages ─────────────────────────────────────────────
  "employee-master": {
    label: "Employee Master",
    navigateTo: "/hr/employee-master",
  },
  attendances: { label: "Attendances", navigateTo: "/hr/attendances" },
  attendance: { label: "Attendances", navigateTo: "/hr/attendances" },
  "leave-management": { label: "Leave", navigateTo: "/hr/leave-management" },
  "task-management": { label: "Tasks", navigateTo: "/hr/task-management" },
  "salary-management": { label: "Salary", navigateTo: "/hr/salary-management" },
  "travel-allowance": {
    label: "Allowance",
    navigateTo: "/hr/travel-allowance",
  },
  // /hr/employee-advance-details/:id
  "employee-advance-details": {
    label: "Employee Advance",
    navigateTo: "/hr/employee-advance-details",
  },

  // ── leads sub-pages ──────────────────────────────────────────
  "new-enquiry": { label: "New Enquiry", navigateTo: "/leads/new-enquiry" },
  "follow-ups": { label: "Follow Ups", navigateTo: "/leads/follow-ups" },
  "site-visit": { label: "Site Visit", navigateTo: "/leads/site-visit" },

  // ── inventory sub-pages ──────────────────────────────────────
  product: { label: "Products", navigateTo: "/inventory/product" },
  purchase: { label: "Purchase", navigateTo: "/inventory/purchase" },
  stock: { label: "Stock", navigateTo: "/inventory/stock" },
  damage: { label: "Damage", navigateTo: "/inventory/damage" },

  // ── carpenters sub-pages ─────────────────────────────────────
  "carpenter-master": {
    label: "Carpenter Master",
    navigateTo: "/carpenters/carpenter-master",
  },
  // /carpenters/carpenter-advance-details/:id — not navigable as a list
  "carpenter-advance-details": { label: "Carpenter Advance", navigateTo: null },

  // ── distributor ───────────────────────────────────────────────
  // /distributor is its own top-level page
  distributor: { label: "Distributors", navigateTo: "/distributor" },

  // ── dealer (legacy) ──────────────────────────────────────────
  dealer: { label: "Dealers", navigateTo: "/dealer" },

  // ── projects ─────────────────────────────────────────────────
  track: { label: "Track", navigateTo: null },

  // ── clients sub-pages ────────────────────────────────────────
  details: { label: "Details", navigateTo: null },

  // ── action segments (never navigable on their own) ───────────
  add: { label: "Add", navigateTo: null },
  edit: { label: "Edit", navigateTo: null },
  view: { label: "View", navigateTo: null },
  generate: { label: "Generate Salary", navigateTo: null },

  // ── misc ─────────────────────────────────────────────────────
  hikvision: { label: "CCTV Camera", navigateTo: "/hikvision" },
  "product-tracking": {
    label: "Product Tracking",
    navigateTo: "/product-tracking",
  },
  promotions: { label: "Promotions", navigateTo: "/promotions" },
};

const Navbar = () => {
  const { clearPermissions } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  const [showSearch, setShowSearch] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(Boolean(document.fullscreenElement));
  const [fullscreenPreferred, setFullscreenPreferred] = useState(
    () => localStorage.getItem("erp-fullscreen-preferred") !== "false",
  );

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  // Browsers only allow Fullscreen API after a user gesture. Harwin therefore
  // defaults to fullscreen preference ON and enters fullscreen on the first
  // click/touch. The navbar button can always switch it off/on afterwards.
  useEffect(() => {
    if (!fullscreenPreferred || document.fullscreenElement) return undefined;
    const enterOnFirstInteraction = async () => {
      try {
        if (!document.fullscreenElement) {
          await document.documentElement.requestFullscreen();
        }
      } catch (error) {
        console.debug("Fullscreen requires a browser-approved user gesture.", error);
      }
    };
    document.addEventListener("pointerdown", enterOnFirstInteraction, { once: true });
    return () => document.removeEventListener("pointerdown", enterOnFirstInteraction);
  }, [fullscreenPreferred]);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        setFullscreenPreferred(true);
        localStorage.setItem("erp-fullscreen-preferred", "true");
        await document.documentElement.requestFullscreen();
      } else {
        setFullscreenPreferred(false);
        localStorage.setItem("erp-fullscreen-preferred", "false");
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error("Fullscreen failed:", error);
      window.harwinNotify?.("Unable to change full screen mode.");
    }
  };

  // ── Global search state ───────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]); // flat [{type,id,title,subtitle,path}]
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounceRef = useRef(null);

  // ── Breadcrumb ID states ─────────────────────────────────────

  // ── existing ──────────────────────────────────────────────────
  const [breadcrumbEmpId, setBreadcrumbEmpId] = useState(null);
  const [breadcrumbDealerId, setBreadcrumbDealerId] = useState(null);
  const [breadcrumbVehicleId, setBreadcrumbVehicleId] = useState(null);
  const [breadcrumbAttendanceEmployeeId, setBreadcrumbAttendanceEmployeeId] =
    useState(null);
  const [breadcrumbProjectId, setBreadcrumbProjectId] = useState(null);
  const [breadcrumbRoleName, setBreadcrumbRoleName] = useState(null);
  const [breadcrumbUserId, setBreadcrumbUserId] = useState(null);
  const [breadcrumbBrandName, setBreadcrumbBrandName] = useState(null);
  const [breadcrumbAdvanceEmpId, setBreadcrumbAdvanceEmpId] = useState(null);
  const [breadcrumbClientId, setBreadcrumbClientId] = useState(null);

  // ── NEW ────────────────────────────────────────────────────────
  // /distributor/edit|view/:id  → dealerName / distributorName
  const [breadcrumbDistributorName, setBreadcrumbDistributorName] =
    useState(null);
  // /carpenters/carpenter-master/edit/:id  → carpenterId e.g. "CARP-001"
  const [breadcrumbCarpenterEditId, setBreadcrumbCarpenterEditId] =
    useState(null);
  // /carpenters/carpenter-advance-details/:id  → carpenterId
  const [breadcrumbCarpAdvanceCarpId, setBreadcrumbCarpAdvanceCarpId] =
    useState(null);
  // /inventory/product/edit/:id  → productId e.g. "PRD-001"
  const [breadcrumbProductId, setBreadcrumbProductId] = useState(null);

  const searchRef = useRef(null);
  const profileRef = useRef(null);
  const colorPickerRef = useRef(null);

  // Theme
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "system");

  // Backgrounds
  const [erpLightBg, setErpLightBg] = useState(
    localStorage.getItem("erp-light-bg") || ERP_LIGHT_DEFAULT,
  );
  const [erpDarkBg, setErpDarkBg] = useState(
    localStorage.getItem("erp-dark-bg") || ERP_DARK_DEFAULT,
  );
  const [erpLightBgImg, setErpLightBgImg] = useState(
    localStorage.getItem("erp-light-bg-img") || "",
  );
  const [erpDarkBgImg, setErpDarkBgImg] = useState(
    localStorage.getItem("erp-dark-bg-img") || "",
  );

  // User Data
  const [userData, setUserData] = useState({
    fullName: "User",
    role: "Loading...",
    userID: "",
    branch: "",
    branchName: "",
    firstLetter: "U",
    lastLogin: null,
  });

  // ── Load user data ──────────────────────────────────────────
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const user = JSON.parse(userStr);
          let branchName = "";
          if (user.branch) {
            if (typeof user.branch === "object" && user.branch.branchName) {
              branchName = user.branch.branchName;
            } else if (typeof user.branch === "string") {
              if (
                user.branch.length === 24 &&
                /^[a-f0-9]{24}$/i.test(user.branch)
              ) {
                try {
                  const res = await axios.get(`${Apiurl}/branch`);
                  const list = Array.isArray(res.data)
                    ? res.data
                    : res.data?.data || [];
                  const match = list.find(
                    (b) => String(b._id) === String(user.branch),
                  );
                  branchName = match?.branchName || user.branch;
                } catch {
                  branchName = user.branch;
                }
              } else {
                branchName = user.branch;
              }
            }
          }
          const firstName = user.fullName || "User";
          const firstLetter = firstName.charAt(0).toUpperCase();
          setUserData({
            fullName: firstName,
            role: user.role || "User",
            userID: user.userID || "",
            branch: user.branch || "",
            branchName,
            firstLetter,
            lastLogin: user.lastLogin || null,
          });
        }
      } catch (error) {
        console.error("Error loading user data:", error);
      }
    };
    loadUserData();
  }, []);

  // ── Apply theme & background ────────────────────────────────
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const isDark =
      theme === "dark" ||
      (theme === "system" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches);
    const bgColor = isDark ? erpDarkBg : erpLightBg;
    const bgImg = isDark ? erpDarkBgImg : erpLightBgImg;
    root.style.setProperty("--erp-bg-page", bgColor);
    if (bgImg) {
      root.style.setProperty("--erp-bg-image", `url('${bgImg}')`);
      body.style.backgroundImage = `url('${bgImg}')`;
      body.style.backgroundColor = bgColor;
      body.style.backgroundSize = "auto";
      body.style.backgroundRepeat = "repeat";
    } else {
      root.style.setProperty("--erp-bg-image", "none");
      body.style.backgroundImage = "none";
      body.style.backgroundColor = bgColor;
    }
    root.classList.remove("light", "dark");
    if (theme === "light") root.classList.add("light");
    else if (theme === "dark") root.classList.add("dark");
  }, [theme, erpLightBg, erpDarkBg, erpLightBgImg, erpDarkBgImg]);

  // ── System theme listener ────────────────────────────────────
  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e) => {
      if (theme === "system") {
        document.documentElement.classList.toggle("dark", e.matches);
        document.documentElement.classList.toggle("light", !e.matches);
      }
    };
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, [theme]);

  // ── Click outside ────────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        showSearch &&
        searchRef.current &&
        !searchRef.current.contains(e.target)
      ) {
        setShowSearch(false);
        setSearchQuery("");
        setSearchResults([]);
      }
      if (
        showProfileMenu &&
        profileRef.current &&
        !profileRef.current.contains(e.target)
      )
        setShowProfileMenu(false);
      if (
        showColorPicker &&
        colorPickerRef.current &&
        !colorPickerRef.current.contains(e.target)
      )
        setShowColorPicker(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSearch, showProfileMenu, showColorPicker]);

  // ── Global search: debounced fetch against /search ───────────
  const runGlobalSearch = useCallback(async (q) => {
    if (!q.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${Apiurl}/search`, {
        params: { q },
        headers: { Authorization: `Bearer ${token}` },
      });
      // Expect: { success, data: [{ type, id, title, subtitle, path }] }
      setSearchResults(data?.data || data || []);
    } catch (error) {
      console.error("Global search failed:", error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => runGlobalSearch(value), 300);
  };

  const handleSearchResultClick = (result) => {
    setShowSearch(false);
    setSearchQuery("");
    setSearchResults([]);
    if (result.path) navigate(result.path);
  };

  const closeSearch = () => {
    setShowSearch(false);
    setSearchQuery("");
    setSearchResults([]);
  };

  // Group flat results by type, capped per category, for section headers
  const groupedSearchResults = searchResults.reduce((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    if (acc[r.type].length < SEARCH_RESULTS_PER_CATEGORY) acc[r.type].push(r);
    return acc;
  }, {});
  const searchGroupOrder = Object.keys(SEARCH_TYPE_META).filter(
    (t) => groupedSearchResults[t]?.length,
  );

  const cycleTheme = () => {
    setTheme((prev) => {
      const effectiveDark =
        prev === "dark" ||
        (prev === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
      const next = effectiveDark ? "light" : "dark";
      localStorage.setItem("theme", next);
      return next;
    });
  };

  const selectBackgroundColor = (color) => {
    const effectiveDark =
      theme === "dark" ||
      (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
    if (effectiveDark) {
      setErpDarkBg(color);
      setErpDarkBgImg("");
      localStorage.setItem("erp-dark-bg", color);
      localStorage.removeItem("erp-dark-bg-img");
    } else {
      setErpLightBg(color);
      setErpLightBgImg("");
      localStorage.setItem("erp-light-bg", color);
      localStorage.removeItem("erp-light-bg-img");
    }
  };

  const getThemeIcon = () => {
    if (theme === "light") return <Sun size={20} />;
    if (theme === "dark") return <Moon size={20} />;
    return <SunMoon size={20} />;
  };

  const handleLogout = async () => {
    try {
      // Optional: Call backend logout API
      await axios.post(`${Apiurl}/logout`);
    } catch (error) {
      console.log("Backend logout failed, proceeding with local clear");
    }

    // Clear permissions
    clearPermissions();

    // Clear all auth data properly
    localStorage.clear();

    // Redirect to login
    navigate("/login", { replace: true });
  };

  // ── Segment helpers ──────────────────────────────────────────
  const segments = location.pathname.split("/").filter(Boolean);
  const isMongoId = (s) => /^[a-f\d]{24}$/i.test(s);
  const segPath = segments.join("/"); // stable string dep for useEffect

  // ══════════════════════════════════════════════════════════════
  //   ROUTE PATTERN DETECTORS
  //
  //   Route                                        seg[0]         seg[1]                      seg[2]   seg[3]
  //   ───────────────────────────────────────────  ─────────────  ──────────────────────────  ───────  ──────
  //   /hr/employee-master/edit|view/:id            hr             employee-master             edit     :id
  //   /dealer/edit|view/:id                        dealer         edit                        :id
  //   /vehicles/edit|view/:id                      vehicles       edit                        :id
  //   /hr/attendance/view/:id                      hr             attendance                  view     :id
  //   /projects/edit|view|track/:id                projects       edit                        :id
  //   /roles/edit/:id                              roles          edit                        :id
  //   /master/users/edit|view/:id                  master         users                       edit     :id
  //   /brand/edit/:id                              brand          edit                        :id
  //   /hr/employee-advance-details/:id             hr             employee-advance-details    :id
  //   /clients/details/:id                         clients        details                     :id
  //   /distributor/edit|view/:id                   distributor    edit|view                   :id
  //   /carpenters/carpenter-master/edit/:id        carpenters     carpenter-master            edit     :id
  //   /carpenters/carpenter-advance-details/:id    carpenters     carpenter-advance-details   :id
  //   /inventory/product/edit/:id                  inventory      product                     edit     :id
  // ══════════════════════════════════════════════════════════════

  const isEmployeeEditOrView =
    segments[0] === "hr" &&
    segments[1] === "employee-master" &&
    (segments[2] === "edit" || segments[2] === "view") &&
    segments[3] &&
    isMongoId(segments[3]);

  const isDealerEditOrView =
    segments[0] === "dealer" &&
    (segments[1] === "edit" || segments[1] === "view") &&
    segments[2] &&
    isMongoId(segments[2]);

  const isVehicleEditOrView =
    segments[0] === "vehicles" &&
    (segments[1] === "edit" || segments[1] === "view") &&
    segments[2] &&
    isMongoId(segments[2]);

  const isAttendanceView =
    segments[0] === "hr" &&
    segments[1] === "attendance" &&
    segments[2] === "view" &&
    segments[3] &&
    isMongoId(segments[3]);

  const isProjectEditOrViewOrTrack =
    segments[0] === "projects" &&
    (segments[1] === "edit" ||
      segments[1] === "view" ||
      segments[1] === "track") &&
    segments[2] &&
    isMongoId(segments[2]);

  const isRoleEdit =
    segments[0] === "roles" &&
    segments[1] === "edit" &&
    segments[2] &&
    isMongoId(segments[2]);

  const isUserEditOrView =
    segments[0] === "master" &&
    segments[1] === "users" &&
    (segments[2] === "edit" || segments[2] === "view") &&
    segments[3] &&
    isMongoId(segments[3]);

  const isBrandEdit =
    segments[0] === "brand" &&
    segments[1] === "edit" &&
    segments[2] &&
    isMongoId(segments[2]);

  const isEmployeeAdvanceDetails =
    segments[0] === "hr" &&
    segments[1] === "employee-advance-details" &&
    segments[2] &&
    isMongoId(segments[2]);

  const isClientDetails =
    segments[0] === "clients" &&
    segments[1] === "details" &&
    segments[2] &&
    isMongoId(segments[2]);

  // ── NEW ────────────────────────────────────────────────────────

  // /distributor/edit|view/:id
  const isDistributorEditOrView =
    segments[0] === "distributor" &&
    (segments[1] === "edit" || segments[1] === "view") &&
    segments[2] &&
    isMongoId(segments[2]);

  // /carpenters/carpenter-master/edit/:id
  const isCarpenterMasterEdit =
    segments[0] === "carpenters" &&
    segments[1] === "carpenter-master" &&
    segments[2] === "edit" &&
    segments[3] &&
    isMongoId(segments[3]);

  // /carpenters/carpenter-advance-details/:id
  const isCarpenterAdvanceDetails =
    segments[0] === "carpenters" &&
    segments[1] === "carpenter-advance-details" &&
    segments[2] &&
    isMongoId(segments[2]);

  // /inventory/product/edit/:id
  const isProductEdit =
    segments[0] === "inventory" &&
    segments[1] === "product" &&
    segments[2] === "edit" &&
    segments[3] &&
    isMongoId(segments[3]);

  // ── Fetch human-readable IDs ──────────────────────────────────

  useEffect(() => {
    if (!isEmployeeEditOrView) {
      setBreadcrumbEmpId(null);
      return;
    }
    let c = false;
    axios
      .get(`${Apiurl}/employee/${segments[3]}`)
      .then((r) => {
        if (!c) setBreadcrumbEmpId(r.data?.empId || r.data?.empID || null);
      })
      .catch(() => {
        if (!c) setBreadcrumbEmpId(null);
      });
    return () => {
      c = true;
    };
  }, [isEmployeeEditOrView, segPath]);

  useEffect(() => {
    if (!isDealerEditOrView) {
      setBreadcrumbDealerId(null);
      return;
    }
    let c = false;
    axios
      .get(`${Apiurl}/dealer/${segments[2]}`)
      .then((r) => {
        if (!c) setBreadcrumbDealerId(r.data?.dealerId || null);
      })
      .catch(() => {
        if (!c) setBreadcrumbDealerId(null);
      });
    return () => {
      c = true;
    };
  }, [isDealerEditOrView, segPath]);

  useEffect(() => {
    if (!isVehicleEditOrView) {
      setBreadcrumbVehicleId(null);
      return;
    }
    let c = false;
    axios
      .get(`${Apiurl}/vehicle/${segments[2]}`)
      .then((r) => {
        if (!c) setBreadcrumbVehicleId(r.data?.data?.vehicleId || null);
      })
      .catch(() => {
        if (!c) setBreadcrumbVehicleId(null);
      });
    return () => {
      c = true;
    };
  }, [isVehicleEditOrView, segPath]);

  useEffect(() => {
    if (!isAttendanceView) {
      setBreadcrumbAttendanceEmployeeId(null);
      return;
    }
    let c = false;
    axios
      .get(`${Apiurl}/attendance/${segments[3]}`)
      .then((r) => {
        if (!c) setBreadcrumbAttendanceEmployeeId(r.data?.employeeId || null);
      })
      .catch(() => {
        if (!c) setBreadcrumbAttendanceEmployeeId(null);
      });
    return () => {
      c = true;
    };
  }, [isAttendanceView, segPath]);

  useEffect(() => {
    if (!isProjectEditOrViewOrTrack) {
      setBreadcrumbProjectId(null);
      return;
    }
    let c = false;
    axios
      .get(`${Apiurl}/project/${segments[2]}`)
      .then((r) => {
        if (!c) setBreadcrumbProjectId(r.data?.projectId || null);
      })
      .catch(() => {
        if (!c) setBreadcrumbProjectId(null);
      });
    return () => {
      c = true;
    };
  }, [isProjectEditOrViewOrTrack, segPath]);

  useEffect(() => {
    if (!isRoleEdit) {
      setBreadcrumbRoleName(null);
      return;
    }
    const stateRole = window.history.state?.usr?.role;
    if (stateRole?.roleName) {
      setBreadcrumbRoleName(stateRole.roleName);
      return;
    }
    let c = false;
    axios
      .get(`${Apiurl}/roles/${segments[2]}`)
      .then((r) => {
        if (!c) setBreadcrumbRoleName(r.data?.roleName || null);
      })
      .catch(() => {
        if (!c) setBreadcrumbRoleName(null);
      });
    return () => {
      c = true;
    };
  }, [isRoleEdit, segPath]);

  useEffect(() => {
    if (!isUserEditOrView) {
      setBreadcrumbUserId(null);
      return;
    }
    let c = false;
    axios
      .get(`${Apiurl}/users/${segments[3]}`)
      .then((r) => {
        if (!c)
          setBreadcrumbUserId(
            r.data?.userID || r.data?.userId || r.data?.username || null,
          );
      })
      .catch(() => {
        if (!c) setBreadcrumbUserId(null);
      });
    return () => {
      c = true;
    };
  }, [isUserEditOrView, segPath]);

  useEffect(() => {
    if (!isBrandEdit) {
      setBreadcrumbBrandName(null);
      return;
    }
    let c = false;
    axios
      .get(`${Apiurl}/brand/${segments[2]}`)
      .then((r) => {
        if (!c) setBreadcrumbBrandName(r.data?.brandName || null);
      })
      .catch(() => {
        if (!c) setBreadcrumbBrandName(null);
      });
    return () => {
      c = true;
    };
  }, [isBrandEdit, segPath]);

  useEffect(() => {
    if (!isEmployeeAdvanceDetails) {
      setBreadcrumbAdvanceEmpId(null);
      return;
    }
    let c = false;
    axios
      .get(`${Apiurl}/employee/${segments[2]}`)
      .then((r) => {
        if (!c)
          setBreadcrumbAdvanceEmpId(r.data?.empId || r.data?.empID || null);
      })
      .catch(() => {
        if (!c) setBreadcrumbAdvanceEmpId(null);
      });
    return () => {
      c = true;
    };
  }, [isEmployeeAdvanceDetails, segPath]);

  useEffect(() => {
    if (!isClientDetails) {
      setBreadcrumbClientId(null);
      return;
    }
    let c = false;
    axios
      .get(`${Apiurl}/client/${segments[2]}`)
      .then((r) => {
        if (!c) setBreadcrumbClientId(r.data?.clientId || null);
      })
      .catch(() => {
        if (!c) setBreadcrumbClientId(null);
      });
    return () => {
      c = true;
    };
  }, [isClientDetails, segPath]);

  // ── NEW fetchers ───────────────────────────────────────────────

  // /distributor/edit|view/:id → distributorName or dealerName
  useEffect(() => {
    if (!isDistributorEditOrView) {
      setBreadcrumbDistributorName(null);
      return;
    }
    let c = false;
    axios
      .get(`${Apiurl}/dealer/${segments[2]}`)
      .then((r) => {
        if (!c)
          setBreadcrumbDistributorName(
            r.data?.distributorName || r.data?.dealerName || null,
          );
      })
      .catch(() => {
        if (!c) setBreadcrumbDistributorName(null);
      });
    return () => {
      c = true;
    };
  }, [isDistributorEditOrView, segPath]);

  // /carpenters/carpenter-master/edit/:id → carpenterId e.g. "CARP-001"
  useEffect(() => {
    if (!isCarpenterMasterEdit) {
      setBreadcrumbCarpenterEditId(null);
      return;
    }
    let c = false;
    axios
      .get(`${Apiurl}/carpenters/${segments[3]}`)
      .then((r) => {
        const d = r.data?.data || r.data;
        if (!c) setBreadcrumbCarpenterEditId(d?.carpenterId || null);
      })
      .catch(() => {
        if (!c) setBreadcrumbCarpenterEditId(null);
      });
    return () => {
      c = true;
    };
  }, [isCarpenterMasterEdit, segPath]);

  // /carpenters/carpenter-advance-details/:id → carpenterId
  // The :id here is the carpenter's mongo _id, so we can hit /carpenters/:id directly
  useEffect(() => {
    if (!isCarpenterAdvanceDetails) {
      setBreadcrumbCarpAdvanceCarpId(null);
      return;
    }
    let c = false;
    axios
      .get(`${Apiurl}/carpenters/${segments[2]}`)
      .then((r) => {
        const d = r.data?.data || r.data;
        if (!c) setBreadcrumbCarpAdvanceCarpId(d?.carpenterId || null);
      })
      .catch(() => {
        if (!c) setBreadcrumbCarpAdvanceCarpId(null);
      });
    return () => {
      c = true;
    };
  }, [isCarpenterAdvanceDetails, segPath]);

  // /inventory/product/edit/:id → productId e.g. "PRD-001"
  useEffect(() => {
    if (!isProductEdit) {
      setBreadcrumbProductId(null);
      return;
    }
    let c = false;
    axios
      .get(`${Apiurl}/product/${segments[3]}`)
      .then((r) => {
        if (!c) setBreadcrumbProductId(r.data?.productId || null);
      })
      .catch(() => {
        if (!c) setBreadcrumbProductId(null);
      });
    return () => {
      c = true;
    };
  }, [isProductEdit, segPath]);

  // ═══════════════════════════════════════════════════════════
  //   BUILD BREADCRUMBS
  //
  //   Route                                        Breadcrumb shown
  //   ───────────────────────────────────────────  ──────────────────────────────────────────────
  //   /brand/edit/:id                              Brand Master / Edit › Asian Paints
  //   /brand/add                                   Brand Master / Add
  //   /hr/employee-advance-details/:id             HR / Employee Advance › EMP-001
  //   /clients/details/:id                         Clients / Details › CLT-001
  //   /distributor/edit/:id                        Distributors / Edit › Raj Distributors
  //   /distributor/view/:id                        Distributors / View › Raj Distributors
  //   /carpenters/carpenter-master/edit/:id        Carpenters / Carpenter Master / Edit › CARP-001
  //   /carpenters/carpenter-advance-details/:id    Carpenters / Carpenter Advance › CARP-001
  //   /inventory/product/edit/:id                  Inventory / Products / Edit › PRD-001
  //   /roles/edit/:id                              Roles / Edit › HR Manager
  //   /master/users/edit/:id                       Master / Users / Edit › USR-001
  //   /hr/employee-master/edit/:id                 HR / Employee Master / Edit › EMP-001
  //   /projects/track/:id                          Projects / Track › PRJ-001
  // ═══════════════════════════════════════════════════════════
  const buildBreadcrumbs = () => {
    const crumbs = [];

    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];

      // ── MongoId → replace with human-readable label ──────────
      if (isMongoId(seg)) {
        let humanId = seg; // raw fallback

        // ── existing ─────────────────────────────────────────────
        if (isEmployeeEditOrView && i === 3) humanId = breadcrumbEmpId ?? "...";
        else if (isDealerEditOrView && i === 2)
          humanId = breadcrumbDealerId ?? "...";
        else if (isVehicleEditOrView && i === 2)
          humanId = breadcrumbVehicleId ?? "...";
        else if (isAttendanceView && i === 3)
          humanId = breadcrumbAttendanceEmployeeId ?? "...";
        else if (isProjectEditOrViewOrTrack && i === 2)
          humanId = breadcrumbProjectId ?? "...";
        else if (isRoleEdit && i === 2) humanId = breadcrumbRoleName ?? "...";
        else if (isUserEditOrView && i === 3)
          humanId = breadcrumbUserId ?? "...";
        else if (isBrandEdit && i === 2) humanId = breadcrumbBrandName ?? "...";
        else if (isEmployeeAdvanceDetails && i === 2)
          humanId = breadcrumbAdvanceEmpId ?? "...";
        else if (isClientDetails && i === 2)
          humanId = breadcrumbClientId ?? "...";
        // ── NEW ───────────────────────────────────────────────────
        else if (isDistributorEditOrView && i === 2)
          humanId = breadcrumbDistributorName ?? "...";
        else if (isCarpenterMasterEdit && i === 3)
          humanId = breadcrumbCarpenterEditId ?? "...";
        else if (isCarpenterAdvanceDetails && i === 2)
          humanId = breadcrumbCarpAdvanceCarpId ?? "...";
        else if (isProductEdit && i === 3)
          humanId = breadcrumbProductId ?? "...";

        // Merge into previous crumb: "Edit › Raj Distributors"
        if (crumbs.length > 0) {
          crumbs[crumbs.length - 1] = {
            ...crumbs[crumbs.length - 1],
            label: `${crumbs[crumbs.length - 1].label} › ${humanId}`,
            navigateTo: null,
          };
        }
        continue;
      }

      // ── Normal segment ───────────────────────────────────────
      const config = SEGMENT_CONFIG[seg];
      const label = config
        ? config.label
        : seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, " ");
      const navigateTo = config
        ? config.navigateTo
        : "/" + segments.slice(0, i + 1).join("/");

      crumbs.push({ label, navigateTo });
    }

    // Last crumb is always plain text (no link)
    if (crumbs.length > 0) {
      crumbs[crumbs.length - 1].isLast = true;
    }

    return crumbs;
  };

  const breadcrumbs = buildBreadcrumbs();

  // ── Hide on warehouse pages ──────────────────────────────────
  const isWarehousePage =
    location.pathname === "/warehouse" ||
    location.pathname.startsWith("/warehouse/");

  if (isWarehousePage) return null;

  // ── Render ───────────────────────────────────────────────────
  return (
    <>
      <header
        className="top-navbar"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
          gap: 12,
        }}
      >
        {/* LEFT — Breadcrumbs */}
        <div className="navbar-left" style={{ flexShrink: 0, minWidth: 0 }}>
          <div className="breadcrumbs">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={`${crumb.label}-${index}`}>
                {index > 0 && <span className="breadcrumb-separator"> / </span>}
                {crumb.isLast || crumb.navigateTo === null ? (
                  <span className="breadcrumb-current">{crumb.label}</span>
                ) : (
                  <Link to={crumb.navigateTo} className="breadcrumb-link">
                    {crumb.label}
                  </Link>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* CENTER — Branch Badge */}
        <div
          style={{
            flex: "1 1 auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
            padding: "0 8px",
          }}
        >
          {userData.role !== "Super Admin" && userData.branchName && (
            <div
              className="branch-badge"
              style={{
                padding: "6px 18px",
                background: "linear-gradient(90deg, #00b4db, #8bc34a)",
                color: "white",
                borderRadius: "25px",
                fontSize: "13px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                boxShadow: "0 2px 8px rgba(0,180,219,0.3)",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              <CiLocationOn
                size={18}
                style={{ color: "#ffffff" }}
                strokeWidth={2}
              />
              {userData.branchName}
            </div>
          )}
        </div>

        {/* RIGHT — Icons */}
        <div
          className="navbar-right"
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          {/* Global navbar search removed by requirement. */}
          <div ref={colorPickerRef} style={{ position: "relative" }}>
            <button
              type="button"
              className="nav-icon"
              onClick={() => setShowColorPicker((v) => !v)}
              title="Customize ERP background"
              aria-label="Customize ERP background"
              style={{ border: 0, background: "transparent", cursor: "pointer" }}
            >
              <Palette size={20} />
            </button>
            {showColorPicker && (
              <div
                style={{
                  position: "absolute", right: 0, top: "calc(100% + 10px)", zIndex: 9999,
                  width: 248, padding: 14, borderRadius: 12,
                  background: "var(--erp-card-bg, #fff)",
                  border: "1px solid var(--erp-border, #e5e7eb)",
                  boxShadow: "0 12px 32px rgba(15,23,42,.18)",
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 3 }}>Customize Background</div>
                <div style={{ fontSize: 11, opacity: .65, marginBottom: 10 }}>
                  {theme === "dark" ? "Dark mode colours" : "Light mode colours"}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8 }}>
                  {(theme === "dark" ? DARK_COLORS : LIGHT_COLORS).map((color) => {
                    const selected = (theme === "dark" ? erpDarkBg : erpLightBg) === color;
                    return (
                      <button key={color} type="button" onClick={() => selectBackgroundColor(color)}
                        title={color}
                        style={{ width: 28, height: 28, borderRadius: 8, background: color,
                          border: selected ? "3px solid #01aebe" : "1px solid #cbd5e1",
                          boxShadow: selected ? "0 0 0 2px rgba(1,174,190,.18)" : "none", cursor: "pointer" }} />
                    );
                  })}
                </div>
                <button type="button" onClick={() => selectBackgroundColor(theme === "dark" ? ERP_DARK_DEFAULT : ERP_LIGHT_DEFAULT)}
                  style={{ marginTop: 12, width: "100%", border: "1px solid #d1d5db", borderRadius: 8,
                    padding: "7px 10px", background: "transparent", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
                  Reset {theme === "dark" ? "Dark" : "Light"} Background
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            className="nav-icon"
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit Full Screen" : "Full Screen"}
            aria-label={isFullscreen ? "Exit Full Screen" : "Full Screen"}
            style={{ border: 0, background: "transparent", cursor: "pointer" }}
          >
            {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
          </button>
          {/* Theme toggle */}
          <div
            className="nav-icon theme-toggle"
            onClick={cycleTheme}
            title={`Theme: ${theme}`}
          >
            {getThemeIcon()}
          </div>

          {/* Bell */}
          <div
            className="nav-icon notification-bell"
            onClick={() => setShowNotifications(true)}
            style={{ position: "relative", cursor: "pointer" }}
            title="Notifications"
          >
            <Bell size={20} />
            {unreadCount > 0 && (
              <span
                className="notification-count-badge"
                style={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  minWidth: unreadCount > 9 ? 20 : 17,
                  height: 17,
                  padding: "0 3px",
                  background: "#ef4444",
                  color: "#ffffff",
                  borderRadius: "100px",
                  fontSize: unreadCount > 99 ? "9px" : "10px",
                  fontWeight: "800",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  border: "2px solid var(--erp-bg-page, #f7f8fa)",
                  boxShadow: "0 1px 4px rgba(239,68,68,0.45)",
                  pointerEvents: "none",
                }}
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </div>

          {/* Profile */}
          <div className="profile-container" ref={profileRef}>
            <div
              className="profile-box"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
            >
              <div
                className="profile-avatar"
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #00b4db 0%, #8bc34a 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontWeight: "600",
                  fontSize: "16px",
                  textTransform: "uppercase",
                }}
              >
                {userData.firstLetter}
              </div>
              <div className="profile-info">
                <span className="name">{userData.fullName}</span>
                <span className="role">{userData.role}</span>
              </div>
              <ChevronDown
                size={16}
                className={showProfileMenu ? "rotate-180" : ""}
              />
            </div>
            {showProfileMenu && (
              <div className="profile-dropdown">
                <div
                  className="profile-dropdown-header"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <div className="profile-dropdown-user">
                    <strong>{userData.fullName}</strong>
                    <span className="profile-dropdown-role">
                      {userData.role}
                    </span>
                    <span className="profile-dropdown-id">
                      {userData.userID}
                    </span>
                    {userData.branchName ? (
                      <span className="profile-dropdown-meta">
                        Branch: {userData.branchName}
                      </span>
                    ) : null}
                    <span className="profile-dropdown-meta">
                      Last login:{" "}
                      {userData.lastLogin
                        ? new Date(userData.lastLogin).toLocaleString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })
                        : "First login"}
                    </span>
                  </div>
                  <button
                    onClick={handleLogout}
                    title="Logout"
                    style={{
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      border: "1px solid #fca5a5",
                      background: "#fee2e2",
                      color: "#ef4444",
                      cursor: "pointer",
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#ef4444";
                      e.currentTarget.style.color = "#fff";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#fee2e2";
                      e.currentTarget.style.color = "#ef4444";
                    }}
                  >
                    <LogOut size={15} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ===================== APP FOOTER ===================== */}
      <footer
        className="app-footer"
        style={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "8px 20px",
          fontSize: "14px",
        }}
      >
        <span className="app-footer-credit">
          Developed by <strong>{FOOTER_DEVELOPER_NAME}</strong>
          {FOOTER_DEVELOPER_LOCATION ? ` - ${FOOTER_DEVELOPER_LOCATION}` : ""}
        </span>
      </footer>

      {/* Notification Panel */}
      <NotificationPanel
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
        onUnreadCountChange={setUnreadCount}
      />
    </>
  );
};

export default Navbar;
